import React, { useCallback, useRef, useState, useEffect } from "react";
import {
  Container,
  Card,
  Button,
  Form,
  Spinner,
  Badge,
  ListGroup,
} from "react-bootstrap";
import { QrReader } from "react-qr-reader";
import { eventAccessApi } from "../Components/eventAccessApi";

// MUI icons
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoIcon from "@mui/icons-material/Info";
import HistoryIcon from "@mui/icons-material/History";
import ReplayIcon from "@mui/icons-material/Replay";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import styles from "./EventScanner.module.scss";

/* small helper: beep */
function useBeep() {
  return useCallback((ok) => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.type = "sine";
      o.frequency.value = ok ? 880 : 220;
      g.gain.setValueAtTime(0.18, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.18);
      o.start(ctx.currentTime);
      o.stop(ctx.currentTime + 0.18);
    } catch (_) {}
  }, []);
}

/* vibrate if available */
function useVibrate() {
  return useCallback((ok) => {
    try {
      if (navigator.vibrate) navigator.vibrate(ok ? [40] : [80, 40, 80]);
    } catch (_) {}
  }, []);
}

/* format timestamp */
function fmtTime(ts) {
  const d = new Date(ts);
  return d.toLocaleString();
}

/* quick heuristic to detect suspicious token shapes */
function isSuspiciousToken(token) {
  if (!token || typeof token !== "string") return true;
  // expected: base64-ish or alphanumeric with -_
  const clean = token.split("/").pop().split("?")[0].trim();
  if (clean.length < 8) return true;
  // suspicious if contains long punctuation sequences or spaces
  if (/\s/.test(clean)) return true;
  if (/[^A-Za-z0-9\-_]/.test(clean) && !/^https?:\/\//.test(clean)) return true;
  return false;
}

export default function EventScanner() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [manualToken, setManualToken] = useState("");
  const [cameraError, setCameraError] = useState(null);
  const [recentScans, setRecentScans] = useState([]); // newest first
  const lastScanned = useRef("");
  const playBeep = useBeep();
  const vibrate = useVibrate();

  /* Keep scan count (quick map in ref so UI updates via setRecentScans) */
  const scanCounts = useRef({});

  /* Push to history safely, keep max length */
  const pushHistory = (item) => {
    setRecentScans((prev) => {
      const next = [item, ...prev].slice(0, 12);
      return next;
    });
  };

  /* Main scan handler */
  const handleScan = useCallback(
    async (raw) => {
      if (!raw) return;
      let token = (raw || "").toString().trim();
      // support full URLs with /scan/<token>?...
      if (token.includes("/scan/")) {
        token = token.split("/scan/").pop().split("?")[0].trim();
      }
      if (!token) return;
      if (token.length < 6) return;
      // debouncing identical immediate scans
      if (lastScanned.current === token) return;
      lastScanned.current = token;
      setLoading(true);
      setResult(null);

      // pre-check heuristics
      const suspiciousShape = isSuspiciousToken(token);

      // optimistic history entry while waiting
      const pre = { token, status: "validating", timestamp: Date.now(), suspicious: suspiciousShape };
      pushHistory(pre);

      try {
        const res = await eventAccessApi.scan(token); // expects { status: 'valid'|'invalid'|... , student, event, payment_status, message }
        const status = res?.status || "error";

        // decide "fake" heuristic: server says invalid OR shape suspicious -> show possible fake
        const isFake = status === "invalid" || status === "error" || suspiciousShape;

        const final = {
          token,
          status,
          server: res || null,
          timestamp: Date.now(),
          suspicious: suspiciousShape,
          message: res?.message || (isFake ? "Invalid or possibly forged code." : ""),
        };

        // update history (replace top-most validating entry if it matches token)
        setRecentScans((prev) => {
          const newList = prev.slice();
          const idx = newList.findIndex((r) => r.token === token && r.status === "validating");
          if (idx >= 0) newList[idx] = final;
          else newList.unshift(final);
          return newList.slice(0, 12);
        });

        // update counts
        scanCounts.current[token] = (scanCounts.current[token] || 0) + 1;

        setResult(res);
        // feedback
        const ok = res?.status === "valid";
        playBeep(ok);
        vibrate(ok);
      } catch (e) {
        const fail = {
          token,
          status: "error",
          server: null,
          timestamp: Date.now(),
          suspicious: suspiciousShape,
          message: e?.message || "Network error",
        };
        setRecentScans((prev) => [fail, ...prev].slice(0, 12));
        setResult({ status: "error", message: e?.message || "Request failed" });
        playBeep(false);
        vibrate(false);
      } finally {
        setLoading(false);
        setTimeout(() => {
          lastScanned.current = "";
        }, 1200);
      }
    },
    [playBeep, vibrate]
  );

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualToken.trim()) {
      handleScan(manualToken.trim());
      setManualToken("");
    }
  };

  /* small actions in history list */
  const handleCopy = (token) => {
    try {
      navigator.clipboard?.writeText(token);
    } catch (_) {}
  };

  const handleRescan = (token) => {
    if (token) handleScan(token);
  };

  /* helpful keyboard shortcut: paste+enter (Ctrl+V then Enter) handled by form already,
     but allow pressing 'r' to clear result */
  useEffect(() => {
    const onKey = (ev) => {
      if (ev.key === "Escape") {
        setResult(null);
      }
      if (ev.key === "r") {
        setResult(null);
        setRecentScans([]);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <Container fluid className="p-0">
      <div className={styles.scannerContent}>
          <h4>Session Scan — QR Check-in</h4>

          <div className="row g-4">
            {/* Left: Camera + manual input */}
            <div className="col-lg-6">
              <Card className={styles.cameraCard} aria-live="polite">
                <Card.Header>
                  <CameraAltIcon fontSize="small" />
                  Camera
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <Badge bg="light" text="dark" style={{ fontWeight: 600 }}>
                      {recentScans.length} recent
                    </Badge>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setRecentScans([]);
                      }}
                      title="Clear history (Esc also)"
                    >
                      <HistoryIcon fontSize="small" />
                    </Button>
                  </div>
                </Card.Header>

                <Card.Body className="p-0" style={{ position: "relative" }}>
                  <div className={styles.qrReaderContainer}>
                    {cameraError ? (
                      <div className={styles.cameraError}>
                        <CameraAltIcon style={{ fontSize: 48 }} />
                        <p>{cameraError}</p>
                        <p className="small">Allow camera access or paste a token below.</p>
                      </div>
                    ) : (
                      <>
                        <QrReader
                          constraints={{ facingMode: "environment" }}
                          onResult={(result, error) => {
                            if (result?.text) handleScan(result.text);
                            if (error && error.name === "NotAllowedError") {
                              setCameraError("Camera permission denied.");
                            }
                          }}
                          containerStyle={{ width: "100%", height: "100%" }}
                          videoContainerStyle={{ width: "100%", height: "100%" }}
                          videoStyle={{ width: "100%", height: "100%", objectFit: "cover" }}
                          scanDelay={350}
                        />

                        {/* Overlay - scan frame */}
                        <div className={styles.scanOverlay} aria-hidden>
                          <div className={styles.scanFrame} role="presentation">
                            {/* pulse ring when loading */}
                            {loading && <div className={styles.pulseRing} />}
                            <div style={{ color: "#fff", fontSize: 12, textAlign: "center", pointerEvents: "none" }}>
                              Align QR here
                            </div>
                          </div>
                          <div className={styles.scanHint}>Place code in the green frame — hold steady</div>
                        </div>
                      </>
                    )}
                  </div>
                </Card.Body>
              </Card>

              <Form onSubmit={handleManualSubmit} className={styles.manualForm}>
                <div className={styles.inputGroup}>
                  <Form.Control
                    type="text"
                    placeholder="Or paste token / URL"
                    value={manualToken}
                    onChange={(e) => setManualToken(e.target.value)}
                    className={styles.formControl}
                    aria-label="Paste token or URL"
                  />
                  <button type="submit" className={styles.validateButton} disabled={loading}>
                    Validate
                  </button>
                </div>
              </Form>

              {/* Recent scans summary under camera (compact) */}
              <div className={styles.scanHistory} style={{ marginTop: "1rem" }}>
                <div className="historyHeader" style={{ padding: ".6rem .9rem", borderBottom: "1px solid #eef6ff" }}>
                  <small style={{ fontWeight: 700, color: "#0f172a" }}>
                    <HistoryIcon fontSize="small" style={{ marginRight: 8 }} /> Scan history
                  </small>
                </div>

                <div className="historyList">
                  {recentScans.length === 0 ? (
                    <div style={{ padding: "1rem", color: "#64748b" }}>
                      No recent scans yet — they will appear here.
                    </div>
                  ) : (
                    recentScans.map((s, i) => (
                      <div key={s.token + i} className="scanRow" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <div style={{ width: 10 }}>
                          {s.status === "valid" ? (
                            <CheckCircleIcon style={{ color: "#10b981" }} fontSize="small" />
                          ) : s.status === "validating" ? (
                            <Spinner animation="border" size="sm" />
                          ) : (
                            <ReportProblemIcon style={{ color: "#ef4444" }} fontSize="small" />
                          )}
                        </div>

                        <div className="meta">
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <div style={{ minWidth: 0 }}>
                              <div className="token" title={s.token} style={{ fontWeight: 600, color: "#0f172a" }}>
                                {s.server?.student?.first_name || s.token}
                              </div>
                              <div className="time" style={{ marginTop: 2 }}>{fmtTime(s.timestamp)}</div>
                            </div>

                            <div style={{ marginLeft: "auto", display: "flex", gap: 6, alignItems: "center" }}>
                              {s.suspicious && <Badge bg="warning" text="dark">Suspicious</Badge>}
                              {s.status === "valid" && <Badge bg="success">Granted</Badge>}
                              {s.status === "already_used" && <Badge bg="secondary">Used</Badge>}
                              {s.status === "expired" && <Badge bg="danger">Expired</Badge>}
                              {s.status === "invalid" && <Badge bg="danger">Invalid</Badge>}
                            </div>
                          </div>

                          <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center" }}>
                            <small style={{ color: "#64748b" }}>{s.server?.event?.title || s.message || ""}</small>
                          </div>
                        </div>

                        <div className="actions" style={{ display: "flex", gap: 6 }}>
                          <Button variant="light" size="sm" onClick={() => handleCopy(s.token)} title="Copy token">
                            <ContentCopyIcon fontSize="small" />
                          </Button>
                          <Button variant="outline-secondary" size="sm" onClick={() => handleRescan(s.token)} title="Rescan">
                            <ReplayIcon fontSize="small" />
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Right: result / info */}
            <div className="col-lg-6">
              {loading && (
                <div className={styles.loadingIndicator}>
                  <Spinner animation="border" />
                  <p>Validating access…</p>
                </div>
              )}

              {!loading && result && result.status === "valid" && (
                <Card className={`${styles.resultCard} ${styles.validCard}`}>
                  <Card.Body>
                    <h5 className="text-success">
                      <CheckCircleIcon color="success" />
                      ACCESS GRANTED
                    </h5>

                    <div className={styles.detailItem}>
                      <strong>Student:</strong>{" "}
                      <span>{[result.student?.first_name, result.student?.last_name].filter(Boolean).join(" ") || "—"}</span>
                    </div>

                    <div className={styles.detailItem}>
                      <strong>Session:</strong> <span>{result.event?.title ?? "—"}</span>
                    </div>

                    <div className={styles.detailItem}>
                      <strong>Payment:</strong>{" "}
                      <span className={`${styles.paymentStatus} ${styles[result.payment_status] || ""}`}>
                        {result.payment_status ?? "—"}
                      </span>
                    </div>

                    <div style={{ marginTop: 8 }}>
                      <Button variant="outline-primary" size="sm" onClick={() => window.open(`/student/${result.student?.id || ""}`, "_blank")}>
                        View profile
                      </Button>{" "}
                      <Button variant="secondary" size="sm" onClick={() => alert("Marked as assisted (demo)")}>
                        Mark assisted
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {!loading && result && result.status !== "valid" && (
                <Card className={`${styles.resultCard} ${styles.invalidCard}`}>
                  <Card.Body>
                    <h5 className="text-danger" style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <CancelIcon color="error" />
                      {result.status === "already_used"
                        ? "QR Already Used"
                        : result.status === "expired"
                        ? "QR Expired"
                        : result.status === "invalid"
                        ? "Access Denied — Possible Fake QR"
                        : "Error"}
                    </h5>

                    <p style={{ marginTop: 8, color: "#475569" }}>
                      {result.message || "This code cannot be used for entry. If you believe this is an error, mark as disputed."}
                    </p>

                    <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
                      <Button variant="outline-danger" size="sm" onClick={() => alert("Dispute flagged (demo)")}>
                        Flag as dispute
                      </Button>
                      <Button variant="light" size="sm" onClick={() => handleRescan(result?.token || "")}>
                        Retry scan
                      </Button>
                    </div>
                  </Card.Body>
                </Card>
              )}

              {!loading && !result && (
                <div className={styles.emptyState}>
                  <InfoIcon />
                  <p>Scan a participant’s session QR code or paste the token above.</p>
                </div>
              )}
            </div>
          </div>
      </div>
    </Container>
  );
}